/**
 * VintageSlider — shared date slider for browsing historical vintage dates.
 *
 * Reusable by any feature that needs vintage navigation (e.g. MacroEconomicTab,
 * MarsLivePage, ReturnsTab): teal accent, play/pause button, step buttons,
 * date labels.
 *
 * Prefetching is optional — consumers pass an `onPrefetch` callback if they
 * want neighboring vintages pre-warmed in the cache.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatShort, formatShortMonthYear } from '@/lib/utils/dates';
import styles from './vintage-slider.module.css';

const PLAY_INTERVAL_MS = 300;

const STEP_BTN_CLASS = [
  'shrink-0 flex items-center justify-center w-[26px] h-[26px]',
  'rounded bg-gray-100 text-gray-700 border border-gray-300',
  'hover:bg-gray-200 hover:border-gray-400 active:scale-95 transition-all',
].join(' ');

export interface VintageSliderProps {
  vintages: string[];
  value: string;
  onChange: (vintage: string) => void;
  /** Optional callback fired when the slider wants to prefetch a vintage. */
  onPrefetch?: (vintage: string) => void;
  /**
   * Register the page-level arrow-key handler (default true). Pass false on
   * every instance but one when the same slider is mounted twice on a page,
   * or each key press steps once per instance.
   */
  keyboardNav?: boolean;
}

export default function VintageSlider({
  vintages,
  value,
  onChange,
  onPrefetch,
  keyboardNav = true,
}: VintageSliderProps): JSX.Element {
  const [isPlaying, setIsPlaying] = useState(false);

  const currentIndex = useMemo(() => {
    const idx = vintages.indexOf(value);
    return idx >= 0 ? idx : vintages.length - 1;
  }, [vintages, value]);

  const firePrefetch = useCallback(
    (index: number) => {
      if (!onPrefetch || !vintages.length) return;
      onPrefetch(vintages[Math.max(0, Math.min(index, vintages.length - 1))]);
    },
    [onPrefetch, vintages],
  );

  const step = useCallback(
    (delta: number) => {
      const nextIdx = Math.max(0, Math.min(vintages.length - 1, currentIndex + delta));
      if (nextIdx === currentIndex) return;
      onChange(vintages[nextIdx]);
      firePrefetch(nextIdx);
    },
    [currentIndex, onChange, firePrefetch, vintages],
  );

  // Page-scoped keyboard arrow navigation. Only elements that consume
  // arrow keys themselves (text entry, selects, radios, the range input's
  // native stepping) keep them; anything else — buttons, checkbox toggles,
  // the page body — steps the slider, so clicking a control doesn't
  // "lose" the slider's keyboard navigation.
  useEffect(() => {
    if (!keyboardNav) return undefined;
    const handler = (e: KeyboardEvent): void => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const el = document.activeElement;
      const tag = (el?.tagName ?? '').toUpperCase();
      if (tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (el instanceof HTMLElement && el.isContentEditable) return;
      if (el instanceof HTMLInputElement && el.type !== 'checkbox') return;
      e.preventDefault();
      step(e.key === 'ArrowLeft' ? -1 : 1);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [step, keyboardNav]);

  // Prefetch on mount / whenever selection changes.
  useEffect(() => {
    firePrefetch(currentIndex);
  }, [currentIndex, firePrefetch]);

  // Play/pause animation walks forward through vintages.
  useEffect(() => {
    if (!isPlaying) return undefined;
    const id = window.setInterval(() => {
      if (currentIndex >= vintages.length - 1) {
        setIsPlaying(false);
        return;
      }
      onChange(vintages[currentIndex + 1]);
    }, PLAY_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isPlaying, currentIndex, onChange, vintages]);

  const progressPct = vintages.length > 1 ? (currentIndex / (vintages.length - 1)) * 100 : 100;

  return (
    <div className="mb-2 mt-1 flex flex-col gap-1" onMouseEnter={() => firePrefetch(currentIndex)}>
      {/* Controls row */}
      <div className="flex items-center gap-3">
        {/* Play/pause button */}
        <button
          type="button"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          onClick={() => setIsPlaying((p) => !p)}
          className={[
            'shrink-0 flex items-center justify-center w-[30px] h-[30px]',
            'rounded-full text-white bg-[color:var(--accent)]',
            'hover:bg-[color:var(--accent-hover)] hover:scale-105',
            'active:scale-95 transition-all',
          ].join(' ')}
        >
          {isPlaying ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M3 2h4v12H3zM9 2h4v12H9z" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M3 2v12l10-6z" />
            </svg>
          )}
        </button>
        <button
          type="button"
          aria-label="Previous vintage"
          onClick={() => step(-1)}
          className={STEP_BTN_CLASS}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
          </svg>
        </button>
        <button
          type="button"
          aria-label="Next vintage"
          onClick={() => step(1)}
          className={STEP_BTN_CLASS}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </button>
        {/* Date label — nudged down slightly so it doesn't sit flush with
         * the play/step buttons; visually balances the controls row. */}
        <div className="mt-[4px] flex-1 text-[14px] font-semibold text-gray-800">
          Forecasts as of {formatShort(value)}
        </div>
        {/* Keyboard hint */}
        <span className="hidden md:inline text-[11px] text-gray-400 shrink-0 whitespace-nowrap">
          Arrow keys navigate
        </span>
      </div>

      {/* Slider track.
       *
       * The wrapper has `py-1` (4px top padding) and `flex flex-col gap-1`, so the
       * native range input — first flex child, `h-[6px]`, `m-0` — sits at y=4-10px
       * inside the wrapper. The absolute gray + accent bars MUST overlay the input
       * exactly so the thumb (centered on the native track via `margin-top: -6px`)
       * appears centered on the visible bar.
       *
       * The overlay bars must use `top:4px` / `h:6px` to line up with the
       * native track — `top-[9px]` would drop them 5px below it, leaving the
       * thumb visibly above the bar.
       */}
      <div className="relative flex flex-col gap-1 py-1">
        <div
          aria-hidden
          className="absolute left-0 right-0 top-[4px] h-[6px] rounded bg-gray-200 pointer-events-none"
        />
        <div
          aria-hidden
          className="absolute left-0 top-[4px] h-[6px] rounded-l bg-[color:var(--accent)] pointer-events-none"
          style={{ width: `${progressPct}%` }}
        />
        <input
          type="range"
          min={0}
          max={vintages.length - 1}
          step={1}
          value={currentIndex}
          onChange={(e) => {
            const idx = Number.parseInt(e.target.value, 10);
            if (!Number.isNaN(idx) && idx !== currentIndex) {
              onChange(vintages[idx]);
              firePrefetch(idx);
            }
          }}
          className={`${styles.input} relative z-[2] w-full h-[6px] bg-transparent rounded appearance-none outline-none cursor-pointer m-0`}
          aria-label="Vintage"
        />
        {/* Date markers */}
        <div className="flex justify-between text-[12px] text-gray-500 px-[2px]">
          <span>{formatShortMonthYear(vintages[0])}</span>
          <span>{formatShortMonthYear(vintages[vintages.length - 1])}</span>
        </div>
      </div>
    </div>
  );
}
